// refSoilSamplingDisturbance id 1 = "Intacta" / Undisturbed. MDC only captures a corer diameter
// for intact samples; every other method makes the operator type the volume in the DapSample task.
const SOIL_SAMPLING_DISTURBANCE_INTACT = 1

// Fixed product rule: the multiplier stays 4 regardless of how many grid points reported data.
export const SOC_SUBSAMPLES_PER_SITE = 4

export type SocGridSamplingPayload = {
  soilSamplingDisturbanceId?: unknown
  sampleDepthCm?: unknown
  sampleDiameterCm?: unknown
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = toNumber(value)
  return parsed !== null && parsed > 0 ? parsed : null
}

const isIntact = (payload: SocGridSamplingPayload | null | undefined): boolean =>
  toNumber(payload?.soilSamplingDisturbanceId) === SOIL_SAMPLING_DISTURBANCE_INTACT

const mean = (values: number[]): number =>
  values.reduce((total, value) => total + value, 0) / values.length

// Kept at the precision the DAP column already uses; the raw double would render as a 15-digit
// float in an editable grid cell.
const corerVolume = (diameterCm: number, depthCm: number): number =>
  Number((Math.PI * (diameterCm / 2) ** 2 * depthCm * SOC_SUBSAMPLES_PER_SITE).toFixed(2))

export const resolveScalarSampleVolume = ({
  explicitSampleVolume,
  gridSamplingPayloads = [],
}: {
  explicitSampleVolume?: unknown
  gridSamplingPayloads?: SocGridSamplingPayload[]
}): number | null => {
  const explicit = toPositiveNumber(explicitSampleVolume)
  if (explicit !== null) return explicit

  // Both means must come from the same grid points: averaging them over different subsets would
  // describe a cylinder no point ever measured.
  const measuredPoints = gridSamplingPayloads
    .filter(isIntact)
    .map(payload => ({
      depth: toPositiveNumber(payload.sampleDepthCm),
      diameter: toPositiveNumber(payload.sampleDiameterCm),
    }))
    .filter(
      (point): point is { depth: number; diameter: number } =>
        point.depth !== null && point.diameter !== null,
    )

  if (measuredPoints.length === 0) return null

  return corerVolume(
    mean(measuredPoints.map(point => point.diameter)),
    mean(measuredPoints.map(point => point.depth)),
  )
}

export const resolveDoubleDepthSampleVolume = ({
  explicitSampleVolume,
  gridSamplingPayload,
  initialDepth,
  finalDepth,
}: {
  explicitSampleVolume?: unknown
  gridSamplingPayload?: SocGridSamplingPayload | null
  initialDepth?: unknown
  finalDepth?: unknown
}): number | null => {
  const explicit = toPositiveNumber(explicitSampleVolume)
  if (explicit !== null) return explicit

  if (!isIntact(gridSamplingPayload)) return null

  const diameter = toPositiveNumber(gridSamplingPayload?.sampleDiameterCm)
  const initial = toNumber(initialDepth)
  const final = toNumber(finalDepth)
  if (diameter === null || initial === null || final === null) return null

  const depth = final - initial
  if (depth <= 0) return null

  return corerVolume(diameter, depth)
}
